/**
 * How a turn was produced, readable: the steps of `assistant_message.debug_trace`
 * (backend/backend/harness/trace/turn_trace.py) and the state before/after (`debug_state`).
 */
import type { AssistantMessage } from '../core/wire';

export interface TraceLine {
  stage: string;
  title: string;
  details: string[];
}

type Step = Record<string, unknown>;

const WRITER_BADGE: Record<string, string> = {
  model: 'NLU', nlg: 'NLG', template: 'TPL', rule: 'RULE', recovery: 'REC', guardrail: 'BLOCK', pipeline: 'PIPE',
};

const text = (v: unknown) => (v === undefined || v === null ? '' : typeof v === 'string' ? v : JSON.stringify(v));
const short = (v: unknown, n = 160) => {
  const s = text(v);
  return s.length > n ? `${s.slice(0, n)}…` : s;
};
const field = (step: Step, key: string, label = key) => (step[key] === undefined ? [] : [`${label}: ${short(step[key])}`]);

export function writerOf(message: AssistantMessage): string | null {
  const outcome = message.debug_trace?.find((s) => s.stage === 'outcome');
  const writer = typeof outcome?.writer === 'string' ? outcome.writer : null;
  return writer ? WRITER_BADGE[writer] ?? writer.toUpperCase() : null;
}

/** The chips under an answer in dev mode: level, time, screens, writer, action. */
export function replyChips(message: AssistantMessage): string[] {
  const chips: string[] = [];
  if (message.nlu_level) chips.push(message.nlu_level);
  if (message.ttfb_ms !== undefined) chips.push(`${Math.round(message.ttfb_ms)} ms`);
  if (message.previous_state && message.state && message.previous_state !== message.state) chips.push(`${message.previous_state}→${message.state}`);
  const writer = writerOf(message);
  if (writer) chips.push(writer);
  const act = message.debug_trace?.find((s) => s.stage === 'act')?.act;
  if (typeof act === 'string') chips.push(act.toUpperCase());
  if (message.action) chips.push(message.action);
  if (message.blocked) chips.push('BLOCKED');
  const sure = sureness(message);
  if (sure) chips.push(sure);
  return chips;
}

/** The confidence gate's verdict (`confidence` on): «SURE 0.82» it acted, «ASKED 0.31<0.5» it asked instead. */
function sureness(message: AssistantMessage): string | null {
  const step = message.debug_trace?.find((s) => s.stage === 'sureness');
  if (!step) return null;
  const value = Number(step.value).toFixed(2);
  return step.held ? `ASKED ${value}<${text(step.least)}` : `SURE ${value}`;
}

export function traceLines(message: AssistantMessage): TraceLine[] {
  return (message.debug_trace ?? []).map((step) => lineOf(step as Step));
}

function lineOf(step: Step): TraceLine {
  const stage = String(step.stage ?? '?');
  switch (stage) {
    case 'input':
      return { stage, title: `input (${text(step.source)}) on ${text(step.state)}`, details: [...field(step, 'text'), ...field(step, 'button'), ...field(step, 'data')] };
    case 'fuzzy':
      return { stage, title: 'normalized', details: [...field(step, 'normalized'), ...field(step, 'rewrites')] };
    case 'act':
      return { stage, title: `act: ${text(step.act)}`, details: [...field(step, 'reason'), ...field(step, 'source')] };
    case 'model_call': {
      const calls = Array.isArray(step.tool_calls) ? step.tool_calls.map((c) => short((c as Step).function ?? c, 120)) : [];
      return {
        stage,
        title: `model call #${text(step.attempt) || 1}${step.ms !== undefined ? ` · ${Math.round(Number(step.ms))} ms` : ''}`,
        details: [...field(step, 'format'), ...calls.map((c) => `call: ${c}`), ...field(step, 'text', 'said'), ...field(step, 'error')],
      };
    }
    case 'model_action':
      return { stage, title: `model chose ${text(step.action)}`, details: [...field(step, 'arguments'), ...field(step, 'confidence'), ...field(step, 'sanity')] };
    case 'nlu':
      return { stage, title: `NLU ${text(step.level)} → ${text(step.action)}`, details: [...field(step, 'arguments'), ...field(step, 'confidence'), ...field(step, 'path')] };
    case 'guardrail':
      return { stage, title: step.passed === false ? `guardrail blocked (${text(step.blocked_by)})` : 'guardrail passed', details: [...field(step, 'message'), ...field(step, 'hint')] };
    case 'sureness':
      return {
        stage,
        title: `sureness ${Number(step.value).toFixed(2)} · min ${text(step.least)} · ${step.held ? 'asked' : 'acted'}`,
        details: [...field(step, 'factors'), ...field(step, 'weakest')],
      };
    case 'discard':
      return { stage, title: `discarded ${text(step.action)}`, details: [...field(step, 'reason'), ...field(step, 'at')] };
    case 'retry':
      return { stage, title: `retry by ${text(step.by)}`, details: field(step, 'reason') };
    case 'level':
      return { stage, title: `freedom ${text(step.level)}`, details: [...field(step, 'origin'), ...field(step, 'downgrade')] };
    case 'reply':
      return { stage, title: `reply by ${text(step.writer)}`, details: [...field(step, 'template'), ...field(step, 'rule'), ...field(step, 'why'), ...field(step, 'text')] };
    case 'reply_edit':
      return { stage, title: `reply edited (${text(step.kind)})`, details: [...field(step, 'before'), ...field(step, 'after')] };
    case 'outcome':
      return { stage, title: `${text(step.state_before)} → ${text(step.state_after)} · ${WRITER_BADGE[text(step.writer)] ?? text(step.writer)}`, details: field(step, 'text') };
    default:
      return { stage, title: stage, details: [short(step, 300)] };
  }
}
